import { createContext, useCallback, useEffect, useState } from "react";
import { apiFetch, apiErrorMessage, readApiBody } from "../services/api";

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Restores a session after a hard refresh. The in-memory `token` never
  // survives a reload, but the httpOnly `gs_refresh` cookie does, so we
  // silently exchange it for a fresh access token via the same endpoint
  // apiFetch already uses for 401 retries, then hydrate the user profile.
  const restoreSession = useCallback(async () => {
    setAuthLoading(true);
    try {
      const refreshRes = await apiFetch("/auth/refresh", { method: "POST" });
      const refreshData = await readApiBody(refreshRes);
      const sessionToken = refreshRes.ok ? refreshData.token || null : null;

      if (!sessionToken) {
        setToken(null);
        setUser(null);
        return;
      }

      const meRes = await apiFetch("/auth/me", {}, { authToken: sessionToken });
      const me = meRes.ok ? await meRes.json() : null;

      setToken(sessionToken);
      setUser(me || null);
    } catch {
      setToken(null);
      setUser(null);
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    restoreSession();
  }, [restoreSession]);

  const login = async (email, password) => {
    const res = await apiFetch("/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const data = await readApiBody(res);
    if (!res.ok) throw new Error(apiErrorMessage(data, "Login failed"));
    setToken(data.token || null);
    setUser(data.user || null);
  };

  const adminLogin = async (email, password) => {
    const res = await apiFetch("/admin/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const data = await readApiBody(res);
    if (!res.ok) throw new Error(apiErrorMessage(data, "Admin login failed"));
    setToken(data.token || null);
    setUser(data.user || null);
  };

  const logout = async () => {
    try {
      await apiFetch(
        "/auth/logout",
        { method: "POST" },
        token ? { authToken: token } : {},
      );
    } catch {
      // Clear local session state even if the server request fails.
    }
    setToken(null);
    setUser(null);
  };

  const forgotPassword = async (email) => {
    const res = await apiFetch("/auth/forgot-password", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    const data = await readApiBody(res);
    if (!res.ok) throw new Error(apiErrorMessage(data, "Unable to request a password reset"));
    return data.message;
  };

  const resetPassword = async (resetToken, password) => {
    const res = await apiFetch("/auth/reset-password", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: resetToken, password }),
    });
    const data = await readApiBody(res);
    if (!res.ok) throw new Error(apiErrorMessage(data, "Unable to reset password"));
    return data.message;
  };

  const register = async (body) => {
    const res = await apiFetch("/auth/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await readApiBody(res);
    if (!res.ok) throw new Error(apiErrorMessage(data, "Registration failed"));
    setToken(data.token || null);
    setUser(data.user || null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        token,
        authLoading,
        login,
        adminLogin,
        logout,
        register,
        forgotPassword,
        resetPassword,
        refreshSession: restoreSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
